import { useCallback, useEffect, useMemo, useRef } from 'react';

import { logger } from '@/lib/logger';

/**
 * Event handlers returned by usePrefetchOnIntent hook.
 */
export interface PrefetchHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: () => void;
}

/**
 * Hook that provides event handlers for intent-based prefetching.
 *
 * Triggers prefetch on:
 * - Mouse hover after a 150ms delay (to avoid prefetching during cursor pass-through)
 * - Mouse down immediately (user is about to click)
 *
 * Features:
 * - Deduplicates prefetch (only prefetches once per instance)
 * - Cancels pending prefetch if user leaves before delay completes
 * - Cleans up timeout on unmount
 * - Silent error handling (prefetch is optimization, not critical)
 *
 * @param prefetchFn - Async function to call when prefetch should trigger
 * @returns Event handlers to spread onto the target element
 *
 * @example
 * ```tsx
 * const handlers = usePrefetchOnIntent(async () => {
 *   await queryClient.prefetchQuery(someQuery);
 * });
 *
 * <button {...handlers}>Click me</button>
 * ```
 */
export function usePrefetchOnIntent(
  prefetchFn: () => Promise<void>
): PrefetchHandlers {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPrefetchedRef = useRef(false);

  const triggerPrefetch = useCallback(() => {
    if (hasPrefetchedRef.current) {
      return;
    }
    hasPrefetchedRef.current = true;
    prefetchFn().catch(() => {
      // Silent catch - prefetch is optimization, not critical
      logger.debug('Prefetch failed');
    });
  }, [prefetchFn]);

  const onMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(triggerPrefetch, 150);
  }, [triggerPrefetch]);

  const onMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const onMouseDown = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    triggerPrefetch();
  }, [triggerPrefetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useMemo(
    () => ({ onMouseEnter, onMouseLeave, onMouseDown }),
    [onMouseEnter, onMouseLeave, onMouseDown]
  );
}
